import React from "react";
import { useConfigurationComponentType } from "../../contexts/ConfigurationComponentType";

const PayrollComponentForm = ({
  closeModal,
  onSubmit,
  initialValues,
  isEditing,
  loading,
  calculationTypes,
}) => {
  const [formState, setFormState] = React.useState(initialValues || {});
  const {component_types, fetchComponentTypes} = useConfigurationComponentType(); 

  // Fetch component types on mount
  React.useEffect(() => {
    fetchComponentTypes();
  }, []);

  // Single useEffect to handle initialValues changes
  React.useEffect(() => {
    if (initialValues) {
      setFormState(initialValues);
    }
  }, [initialValues]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formState);
  };

  const handleInputChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderSelect = (
    label,
    value,
    onChange,
    options,
    placeholder = "Select",
    required = false
  ) => (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {label}{" "}
        {required && <span className="text-red-500">*</span>}
      </label>
      <select
        className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}  
      >
        <option value="">{placeholder}</option>
        {options.map((opt) =>
          typeof opt === "object" && opt.value !== undefined ? (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ) : (
            <option key={opt} value={opt}>
              {opt}
            </option>
          )
        )}
      </select>
    </div>
  );

  // Helper function to get display label for component type
  const getComponentTypeLabel = (value) => {
    if (!value || !component_types) return "";
    const componentType = component_types.find(type => type.id == value);
    return componentType ? (componentType.name || componentType.code || componentType.id) : value;
  };

  // Validate component_types structure
  const validComponentTypes = component_types && Array.isArray(component_types) && 
    component_types.every(type => type && typeof type === 'object' && type.id !== undefined);

  return (
    <div>

      <form onSubmit={handleSubmit} className="flex flex-col ">
        {/* Basic Information Section */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl ">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 pb-2">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Component Name <span className="text-red-500">*</span>
              </label>
              <input
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
                value={formState.component_name || ""}
                onChange={(e) => handleInputChange('component_name', e.target.value)}
                placeholder="e.g., Basic Salary, HRA, PF"
              />
            </div>

            {validComponentTypes && component_types.length > 0 ? (
              renderSelect(
                "Component Type",
                formState.component_type,
                (v) => handleInputChange('component_type', v),
                component_types.map(type => ({
                  value: type.code,
                  label: type.name || type.code || type.id
                })),
                "Select Component Type",
                true
              )
            ) : (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Component Type</label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                  {!component_types ? 'Loading component types...' : 'Invalid component types data'}
                </div>
              </div>
            )}

            {renderSelect(
              "Calculation Type",
              formState.calculation_type,
              (v) => handleInputChange('calculation_type', v),
              calculationTypes,
              "Select Calculation Type",
              true
            )}

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Display Order</label>
              <input
                type="number"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={formState.display_order || ""}
                onChange={(e) => handleInputChange('display_order', e.target.value)}
                placeholder="e.g., 1, 2, 3..."
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={formState.description || ""}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                placeholder="Brief description of the component..."
              />
            </div>
          </div>
        </div>

        {/* Calculation Configuration Section */}
        <div className=" p-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4  pb-2">
            Calculation Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Default Value {formState.calculation_type === 'fixed' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                step="0.01"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={formState.default_value || ""}
                onChange={(e) => handleInputChange('default_value', e.target.value)}
                placeholder="For fixed amount calculations"
                required={formState.calculation_type === 'fixed'}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Percentage (%) {formState.calculation_type === 'percentage' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                step="0.01"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={formState.percentage || ""}
                onChange={(e) => handleInputChange('percentage', e.target.value)}
                placeholder="For percentage calculations"
                required={formState.calculation_type === 'percentage'}
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Formula {formState.calculation_type === 'formula' && <span className="text-red-500">*</span>}
              </label>
              <input
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-mono text-sm"
                value={formState.formula || ""}
                onChange={(e) => handleInputChange('formula', e.target.value)}
                placeholder="e.g., basic_salary * 0.4 or (basic_salary + hra) * 0.12"
                required={formState.calculation_type === 'formula'}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use mathematical expressions with variable names for dynamic calculations
              </p>
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="p-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2">
            Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <input
                id="is_taxable"
                type="checkbox"
                checked={Boolean(formState.is_taxable)}
                onChange={(e) => handleInputChange('is_taxable', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
              />
              <div className="flex flex-col">
                <label htmlFor="is_taxable" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Taxable Component
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Include in tax calculations
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <input
                id="is_active"
                type="checkbox"
                checked={Boolean(formState.is_active)}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
              />
              <div className="flex flex-col">
                <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Active Component
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Enable for payroll calculations
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center justify-end pt-6">
          <button
            type="button"
            onClick={closeModal}
            className="px-8 py-2 hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-8 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              isEditing ? "Update Component" : "Create Component"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PayrollComponentForm;